import { useState, useCallback } from 'react';
import { MatchState } from '../types';
import { getAllMatchPoints } from '../utils/matchHistoryUtils';
import { MANUAL_MATCH_PREFIX } from '../constants';
import { getFullVersion } from '../utils/version';
import { SendError, HttpSendError, classifySendError } from '../errors/sendError';

export type SendStatus = 'idle' | 'sending' | 'success' | 'error';

export interface UseResultSenderReturn {
  sendStatus: SendStatus;
  lastError: SendError | null;
  /** Efface l'erreur courante et remet le statut à idle (piloté par l'UI). */
  clearSendError: () => void;
  sendAndCopyResults: () => Promise<boolean>;
  copyResultsOnly: () => Promise<boolean>;
  /** Resolves true when the callback accepted the result (or there is no callback to reach). */
  sendResultsWithStatus: (status: string, silent?: boolean) => Promise<boolean>;
  shouldSendResults: boolean;     // ← NOUVEAU
  shouldShowValidationUI: boolean; // ← NOUVEAU
  hasCallbackUrl: boolean;
}

/**
 * Génère le JSON complet des résultats du match avec attributs explicites
 * ✅ FORMAT LISIBLE ET MAINTENABLE
 */
export function generateResultsJSON(matchState: MatchState, status: string = 'completed'): object {
  // 🚀 OPTIMISATION : Pré-calculer les données une seule fois
  const players = {
    A: matchState.matchInfo.playerA,
    B: matchState.matchInfo.playerB
  };
  
  // 🚀 OPTIMISATION : Calculer le gagnant une seule fois
  const finalScore = matchState.winner 
    ? `${matchState.setScores[matchState.winner]}-${matchState.setScores[matchState.winner === 'A' ? 'B' : 'A']}`
    : '0-0';

  return {
    // 📦 Métadonnées
    meta: {
      status,
      timestamp: Date.now(),
      version: getFullVersion(), // 🆕 VERSION AUTOMATIQUE (ex: "1.0.5.123")
      exported: new Date().toISOString()
    },

    // 🏟️ Informations du match
    match: {
      id: matchState.matchInfo.matchId,
      competition: matchState.matchInfo.competitionName,
      category: matchState.matchInfo.categoryName,
      draw: matchState.matchInfo.drawName,
      roundType: matchState.matchInfo.roundType || '',
      referee: matchState.matchInfo.refereeName,
      court: matchState.matchInfo.courtName,
      scoring: matchState.matchInfo.scoringType,
      dateTime: matchState.matchInfo.dateTime
    },

    // 👥 Joueurs
    players: {
      A: { 
        name: players.A, 
        color: matchState.playerAColor 
      },
      B: { 
        name: players.B, 
        color: matchState.playerBColor 
      }
    },

    // 🏆 Résultat final
    result: {
      winner: matchState.winner,
      finalScore,
      setScores: {
        playerA: matchState.setScores.A,
        playerB: matchState.setScores.B
      },
      duration: matchState.totalMatchDuration,
      completed: matchState.matchEnded,
      startTime: matchState.matchStartTime
    },

    // 🎮 Jeux avec attributs explicites
    games: matchState.gameResults.map((game, index) => ({
      gameNumber: index + 1,
      winner: game.winner,
      winnerName: game.winner === 'A' ? players.A : players.B,
      scoreA: game.scoreA,
      scoreB: game.scoreB,
      duration: game.duration,
      wonByConduct: game.wonByConduct || false,
      wonByInjury: game.wonByInjury || false
    })),

    // 📊 Historique COMPLET des points avec attributs explicites
    points: getAllMatchPoints(matchState),

    // ⚖️ Sanctions avec attributs explicites (seulement si présentes)
    ...(matchState.conductSanctions.length > 0 && {
      sanctions: matchState.conductSanctions.map((sanction, index) => ({
        sanctionNumber: index + 1,
        player: sanction.player,
        playerName: sanction.player === 'A' ? players.A : players.B,
        sanctionType: sanction.type,
        justification: sanction.justification,
        timestamp: sanction.timestamp || Date.now(),
        gameNumber: sanction.gameNumber || 1,
        gameScore: {
          scoreA: sanction.gameScore?.A || 0,
          scoreB: sanction.gameScore?.B || 0
        }
      }))
    })
  };
}

/**
 * Une réponse au questionnaire post-match.
 * Forme volontairement générique : les questions sont définies au niveau du formulaire.
 * `value` est typé `unknown` côté wire ; le typage strict vit dans la config des questions.
 */
export interface FeedbackAnswer {
  questionId: string;
  value: unknown;
}

/**
 * Génère le payload JSON du questionnaire post-match.
 * Distingué côté backend par `meta.status === 'feedback'`.
 */
export function generateFeedbackJSON(
  matchState: MatchState,
  answers: FeedbackAnswer[]
): object {
  return {
    meta: {
      status: 'feedback',
      timestamp: Date.now(),
      version: getFullVersion(),
      exported: new Date().toISOString()
    },
    match: {
      id: matchState.matchInfo.matchId
    },
    feedback: {
      answers,
      submittedAt: new Date().toISOString()
    }
  };
}

/**
 * Génère les données JSON pour l'envoi silencieux en fin de jeu
 */
export function generateGameEndJSON(matchState: MatchState): object {
  return {
    // Métadonnées minimales
    meta: {
      status: 'game_end',
      timestamp: Date.now(),
      gameNumber: matchState.gameResults.length
    },
    
    // Informations du match (identifiant uniquement)
    match: {
      id: matchState.matchInfo.matchId
    },
    
    // Score actuel des sets
    setScores: {
      playerA: matchState.setScores.A,
      playerB: matchState.setScores.B
    },
    
    // Détail de chaque jeu terminé
    games: matchState.gameResults.map((game, index) => ({
      gameNumber: index + 1,
      winner: game.winner,
      scoreA: game.scoreA,
      scoreB: game.scoreB,
      duration: game.duration
    }))
  };
}

/**
 * Envoie les résultats vers l'URL de callback
 */
export async function sendResultsToCallback(data: object, url: string): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(data),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new HttpSendError(response.status, errorText);
    }

    // Succès - optionnel: traiter la réponse
    await response.json().catch(() => ({}));

  } catch (error) {
    clearTimeout(timeoutId);
    // On laisse remonter l'erreur brute (HttpSendError, AbortError, TypeError réseau...).
    // La classification en message clair vs technique se fait via classifySendError().
    throw error;
  }
}

/**
 * Hook principal pour l'envoi des résultats
 */
export function useResultSender(matchState: MatchState): UseResultSenderReturn {
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const [lastError, setLastError] = useState<SendError | null>(null);

  // AJOUTER
  /**
   * Détermine si le match doit envoyer ses résultats (tous les matchs avec URL)
   */
  const shouldSendResults = Boolean(matchState.matchInfo.resultCallbackUrl);

  /**
   * Détermine si l'UI de validation doit être affichée (seulement matchs avec paramètres)
   */
  const shouldShowValidationUI = Boolean(
    matchState.matchInfo.resultCallbackUrl && 
    !matchState.matchInfo.matchId.startsWith(MANUAL_MATCH_PREFIX)
  );

  // Fonction pour envoyer avec un statut spécifique
  const sendResultsWithStatus = useCallback(async (status: string, silent: boolean = false): Promise<boolean> => {
    // En mode silencieux, ne pas modifier le sendStatus visible
    if (!silent) {
      setSendStatus('sending');
      setLastError(null);
    }
    let sent = false;

    try {
      // 📋 Générer les données avec format explicite
      const resultsData = generateResultsJSON(matchState, status);


      // Si on a une URL de callback, essayer l'envoi
      if (shouldSendResults && matchState.matchInfo.resultCallbackUrl) {
        try {
          await sendResultsToCallback(resultsData, matchState.matchInfo.resultCallbackUrl);
          sent = true;

          if (!silent) {
            setSendStatus('success');
            // Pour le statut "validated", ne pas faire de reset automatique
            if (status !== 'validated') {
              setTimeout(() => {
                setSendStatus('idle');
                setLastError(null);
              }, 3000);
            }
          }
          
        } catch (sendError) {
          console.error('❌ Erreur envoi POST:', sendError);

          if (!silent) {
            setLastError(classifySendError(sendError));
            setSendStatus('error');
            // Pas d'auto-reset ici : c'est la couche UI (SendErrorBanner) qui pilote
            // la temporisation d'effacement (10s, figée si l'arbitre déplie les détails)
            // via clearSendError(). Auto-effacer ici l'empêcherait.
          }
        }
      } else if (!silent) {
        // Si pas d'URL de callback, considérer comme succès sans rien faire
        sent = true;
        setSendStatus('success');
        // Pour le statut "validated", ne pas faire de reset automatique
        if (status !== 'validated') {
          setTimeout(() => {
            setSendStatus('idle');
            setLastError(null);
          }, 3000);
        }
      }

    } catch (error) {
      console.error('❌ Erreur générale:', error);

      if (!silent) {
        setLastError(classifySendError(error));
        setSendStatus('error');
        // Pas d'auto-reset : géré par la couche UI via clearSendError() (cf. ci-dessus).
      }
    }
    return sent;
  }, [matchState, shouldSendResults]);

  const clearSendError = useCallback(() => {
    setLastError(null);
    setSendStatus('idle');
  }, []);

  const sendAndCopyResults = useCallback(async () => {
    return sendResultsWithStatus('validated');
  }, [sendResultsWithStatus]);

  const copyResultsOnly = useCallback(async () => {
    // Cette fonction ne fait plus rien car on supprime la fonctionnalité de copie
    setSendStatus('success');
    
    // Pas de reset automatique pour rester en état "validé"
    return true;
  }, []);

  return {
    sendStatus,
    lastError,
    clearSendError,
    sendAndCopyResults,
    copyResultsOnly,
    sendResultsWithStatus,
    shouldSendResults,        // ← AJOUTER
    shouldShowValidationUI,   // ← AJOUTER
    hasCallbackUrl: shouldShowValidationUI
  };
}